// L2.3 — Persistent color registry across sessions.
//
// L1 used a stateless djb2 hash to pick a palette slot. That guarantees the
// same value (e.g. POD "CNSHA") always renders in the same colour, but it
// makes no effort to spread distinct values across distinct slots: with 10
// palette slots and 3 PODs, three hash-collided PODs can all land on the same
// colour and the planner's eye gets nothing to anchor on.
//
// The registry replaces the hash as the primary source. First-come-first-
// served: each new value claims the lowest free palette index for its scheme,
// the assignment is persisted to localStorage, and subsequent reloads see the
// same colour. The hash stays in place as the fall-back path for when the
// palette is exhausted for that scheme (more than 10 distinct values
// historically seen) — same trade-off as L1.
//
// Per-scheme registries are independent: POD's assignment of slot 0 doesn't
// collide with Operator's slot 0.
//
// Eviction policy is deliberately "none for now": once a value is in the
// registry it stays there until the user explicitly resets. PM hasn't decided
// LRU vs first-seen-wins vs explicit reset button, so we picked the policy
// that is hardest to silently surprise users with. When more than 10 distinct
// values are historically seen, the 11th onwards falls back to hash.
//
// Storage layout: one localStorage key per scheme,
//   `yard:color-by:<scheme>` -> JSON object `{ "<value>": <paletteIndex>, ... }`
// Keys are namespaced so they don't collide with other yard preferences.

const STORAGE_KEY_PREFIX = 'yard:color-by:'

// In-memory mirror of the persisted registry, populated lazily per scheme on
// first access. Subsequent reads hit memory; writes go through to both. This
// matters because `paletteColorFor` is called inside MobX computeds that
// re-evaluate frequently — we don't want every re-evaluation to hammer
// JSON.parse on localStorage.
const cache = new Map<string, Map<string, number>>()

const storageKey = (scheme: string): string => `${STORAGE_KEY_PREFIX}${scheme}`

const hydrate = (scheme: string): Map<string, number> => {
  const cached = cache.get(scheme)
  if (cached) return cached

  const map = new Map<string, number>()
  try {
    const raw = localStorage.getItem(storageKey(scheme))
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
            map.set(key, value)
          }
        }
      }
    }
  } catch {
    // localStorage unavailable (SSR / disabled / corrupted JSON) — start fresh
    // and silently degrade to in-memory-only behaviour for this session.
  }
  cache.set(scheme, map)
  return map
}

const persist = (scheme: string, map: Map<string, number>): void => {
  try {
    const obj: Record<string, number> = {}
    map.forEach((index, value) => {
      obj[value] = index
    })
    localStorage.setItem(storageKey(scheme), JSON.stringify(obj))
  } catch {
    // Quota exceeded or storage disabled — assignment lives in cache only
    // for the remainder of the session. Acceptable: the planner still sees
    // stable colours within the session, just not across reloads.
  }
}

/**
 * Returns the palette index assigned to `value` under `scheme`. If the value
 * has not been seen before, assigns it the lowest currently-free palette
 * index for that scheme and persists the assignment. Returns `null` when all
 * `paletteSize` slots are already claimed for the scheme — caller falls back
 * to a stateless hash.
 */
export const getOrAssignColorIndex = (
  scheme: string,
  value: string,
  paletteSize: number,
): number | null => {
  const map = hydrate(scheme)
  const existing = map.get(value)
  if (existing !== undefined && existing < paletteSize) return existing

  const used = new Set<number>()
  map.forEach(i => {
    if (i < paletteSize) used.add(i)
  })
  for (let i = 0; i < paletteSize; i++) {
    if (!used.has(i)) {
      map.set(value, i)
      persist(scheme, map)
      return i
    }
  }
  return null
}

/**
 * Forget all colour assignments for one scheme (or every scheme if no scheme
 * is given). Exposed for future "reset colours" UI affordances and used by
 * tests to keep cases isolated.
 */
export const clearColorRegistry = (scheme?: string): void => {
  if (scheme !== undefined) {
    cache.delete(scheme)
    try {
      localStorage.removeItem(storageKey(scheme))
    } catch {
      // ignore
    }
    return
  }
  cache.clear()
  try {
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) toRemove.push(key)
    }
    for (const key of toRemove) {
      try {
        localStorage.removeItem(key)
      } catch {
        // ignore — best effort
      }
    }
  } catch {
    // ignore
  }
}
